import React, { useEffect, useMemo, useRef, useState } from "react";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import FinalCTA from "../components/site/FinalCTA";
import FaqAccordion from "../components/site/FaqAccordion";
import { Dot } from "../components/site/Section";
import { REGISTER_URL } from "../data/content";
import { trackCalculatorUse } from "../lib/tracking";

const PRESETS = {
  spirit: { label: "Spirit, 70cl bottle", size: 700, serves: [25, 35, 50], cost: "14.50", price: "4.20" },
  spirit1l: { label: "Spirit, 1 litre bottle", size: 1000, serves: [25, 35, 50], cost: "19.00", price: "4.20" },
  wine: { label: "Wine, 75cl bottle", size: 750, serves: [125, 175, 250], cost: "7.50", price: "7.50" },
  keg: { label: "Draught, 50 litre keg", size: 50000, serves: [284, 568], cost: "120.00", price: "5.80" },
  custom: { label: "Something else", size: null, serves: [] },
};
const SERVE_LABEL = { 284: "Half pint", 568: "Pint" };

export const GP_FAQS = [
  { q: "What is a good GP for a bar?", a: "It depends on your venue and prices, but many UK bars work to around 70% across drinks, with spirits higher and draught lower. Set your own target in the calculator to see the price you need." },
  { q: "Is GP the same as markup?", a: "No. Markup compares profit with what you paid. GP compares profit with what you sell for. A drink with 70% GP has a markup of about 233%." },
  { q: "Should I include VAT?", a: "Use the bottle cost without VAT, and take VAT off the menu price, as the calculator does. If you're not VAT registered, choose that option and the full price counts." },
  { q: "Why allow for waste?", a: "Spillage, over pouring and line cleaning mean you rarely get every serve out of a bottle or keg. Adding a few percent gives a more honest GP." },
];

const money = (v) => (Number.isFinite(v) ? `£${v.toFixed(2)}` : "£0.00");

export default function GpCalculator() {
  const [type, setType] = useState("spirit");
  const [size, setSize] = useState("700");
  const [serve, setServe] = useState("25");
  const [cost, setCost] = useState("14.50");
  const [price, setPrice] = useState("4.20");
  const [vat, setVat] = useState("20");
  const [target, setTarget] = useState("70");
  const [waste, setWaste] = useState("0");
  const tracked = useRef(false);

  const onType = (v) => {
    setType(v);
    const p = PRESETS[v];
    if (p.size) setSize(String(p.size));
    if (p.serves.length) setServe(String(p.serves[0]));
    if (p.cost) { setCost(p.cost); setPrice(p.price); }
  };

  const r = useMemo(() => {
    const S = +size, M = +serve, C = +cost, P = +price, V = +vat, T = +target, W = +waste;
    const ok = S > 0 && M > 0 && C >= 0 && P > 0;
    const cps = ok ? (C / S) * M * (1 + W / 100) : 0;
    const net = ok ? P / (1 + V / 100) : 0;
    const profit = net - cps;
    const gp = net > 0 ? (profit / net) * 100 : 0;
    const serves = ok ? S / M / (1 + W / 100) : 0;
    const tPrice = T > 0 && T < 100 ? (cps / (1 - T / 100)) * (1 + V / 100) : 0;
    return { ok, cps, net, profit, gp, serves, tPrice, T };
  }, [size, serve, cost, price, vat, target, waste]);

  useEffect(() => {
    if (tracked.current) return;
    if (type !== "spirit" || cost !== "14.50" || price !== "4.20") { tracked.current = true; trackCalculatorUse(); }
  }, [type, cost, price]);

  const field = (id, label, value, set, unit, opts = {}) => (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className={`unit ${opts.pre ? "pre" : ""}`}>
        {opts.pre && <span>£</span>}
        <input id={id} type="number" inputMode="decimal" min="0" step={opts.step || "1"} value={value} onChange={(e) => set(e.target.value)} />
        {!opts.pre && unit && <span>{unit}</span>}
      </div>
      {opts.hint && <p className="hint">{opts.hint}</p>}
      {opts.quick}
    </div>
  );

  const quick = PRESETS[type].serves.length > 0 && (
    <div className="quick">
      {PRESETS[type].serves.map((ml) => (
        <button key={ml} type="button" aria-pressed={+serve === ml} onClick={() => setServe(String(ml))}>{SERVE_LABEL[ml] || `${ml}ml`}</button>
      ))}
    </div>
  );

  const status = !r.ok ? "" : r.gp >= r.T ? `At or above your ${r.T}% target` : `${(r.T - r.gp).toFixed(1)} points below your ${r.T}% target`;

  return (
    <div className="App">
      <Navbar />
      <main>
        <section className="page-head" style={{ paddingBottom: 24 }}>
          <div className="wrap">
            <h1><span className="label h1-kicker">Free bar GP calculator</span>Work out the GP on any drink<Dot /></h1>
            <p className="lede">Enter what you pay for the bottle and what you charge for a serve. See your cost per serve, GP and the price you need to hit your target. Free, no sign up.</p>
          </div>
        </section>
        <section style={{ paddingTop: 0 }}>
          <div className="wrap calc-grid">
            <form className="calc" onSubmit={(e) => e.preventDefault()} noValidate>
              <div className="field">
                <label htmlFor="gpType">Drink type</label>
                <select id="gpType" value={type} onChange={(e) => onType(e.target.value)}>
                  {Object.entries(PRESETS).map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}
                </select>
              </div>
              <div className="field-row">
                {field("gpSize", "Bottle or keg size", size, setSize, "ml")}
                {field("gpServe", "Serve size", serve, setServe, "ml", { quick })}
              </div>
              <div className="field-row">
                {field("gpCost", "You pay per bottle or keg", cost, setCost, null, { pre: true, step: "0.01", hint: "Price on the invoice, without VAT." })}
                {field("gpPrice", "You charge per serve", price, setPrice, null, { pre: true, step: "0.05", hint: "Menu price, with VAT." })}
              </div>
              <div className="field-row">
                <div className="field">
                  <label htmlFor="gpVat">VAT on the sell price</label>
                  <select id="gpVat" value={vat} onChange={(e) => setVat(e.target.value)}>
                    <option value="20">20% (standard)</option>
                    <option value="0">Not VAT registered</option>
                  </select>
                </div>
                {field("gpTarget", "Your target GP", target, setTarget, "%")}
              </div>
              {field("gpWaste", "Allow for spillage and waste", waste, setWaste, "%")}
            </form>

            <div className="results" aria-live="polite">
              <div className="gp-big">
                <span className="label">Your GP on this drink</span>
                <b>{r.ok ? `${Math.round(r.gp)}%` : "Add your numbers"}</b>
                <span className={`gp-status ${r.ok ? (r.gp >= r.T ? "good" : "low") : ""}`}>{status}</span>
              </div>
              <dl className="res-list">
                <div><dt>Cost per serve</dt><dd>{money(r.cps)}</dd></div>
                <div><dt>Sell price without VAT</dt><dd>{money(r.net)}</dd></div>
                <div><dt>Profit per serve</dt><dd>{money(r.profit)}</dd></div>
                <div><dt>Serves per bottle or keg</dt><dd>{r.ok ? r.serves.toFixed(1) : "0"}</dd></div>
                <div><dt>Profit per bottle or keg</dt><dd>{money(r.profit * r.serves)}</dd></div>
                <div className="hl"><dt>Price for {r.T || 0}% GP</dt><dd>{money(r.tPrice)}</dd></div>
              </dl>
              <div className="calc-cta">
                <p>Barlo works this out for every drink and cocktail on your menu, and tells you when a supplier price rise eats into your GP.</p>
                <a className="btn btn-green" href={REGISTER_URL}>Try Barlo free for 14 days</a>
              </div>
            </div>
          </div>
        </section>

        <section style={{ paddingTop: 0 }}>
          <div className="wrap explain">
            <div>
              <h2>How to calculate GP on drinks<Dot /></h2>
              <p>Gross profit (GP) is the share of the sell price you keep after paying for the drink itself. In the UK, menu prices include VAT, so take the VAT off first.</p>
              <ol className="formula">
                <li><b>Cost per serve</b> = bottle cost ÷ bottle size × serve size</li>
                <li><b>Sell price without VAT</b> = menu price ÷ 1.2</li>
                <li><b>GP %</b> = (sell price without VAT − cost per serve) ÷ sell price without VAT × 100</li>
              </ol>
              <p><b>Example:</b> a 70cl bottle of gin costs £14.50. A 25ml serve costs £14.50 ÷ 700 × 25 = £0.52. You charge £4.20, which is £3.50 without VAT. GP is (£3.50 − £0.52) ÷ £3.50 = 85%.</p>
              <p>Spirits usually make the highest GP, wine and draught less. Cocktails depend on every ingredient in the recipe, which is why Barlo costs them measure by measure.</p>
            </div>
            <FaqAccordion items={GP_FAQS} testidPrefix="gp-faq" />
          </div>
        </section>
        <FinalCTA testidTrial="gp-cta-trial" />
      </main>
      <Footer />
    </div>
  );
}
